import type { PipelineLoan } from '../types';

export const PERFORMANCE_METRIC_KEYS = ['created', 'application', 'locked', 'funded'] as const;

export type PerformanceMetricKey = typeof PERFORMANCE_METRIC_KEYS[number];
export type CohortBasis = 'application' | 'created';

export interface PerformanceRange {
  from: string;
  to: string;
  label: string;
  businessDays: number;
  includesToday: boolean;
  isEmpty?: boolean;
}

export interface PerformanceAggregate {
  units: number;
  volume: number;
  unitsPerDay: number;
  volumePerDay: number;
}

export interface PerformanceLoanDetail {
  id: string;
  loanNumber: string;
  borrower: string;
  loanOfficer: string;
  eventDate: string;
  amount: number;
  folder: string;
  channel: string;
  createdDate: string | null;
  applicationDate: string | null;
  lockDate: string | null;
  fundedDate: string | null;
  investor?: string | null;
  product?: string | null;
}

export interface PerformanceMetricSummary {
  current: PerformanceAggregate;
  comparison: PerformanceAggregate;
  unitsPerDayDeltaPct: number | null;
  volumePerDayDeltaPct: number | null;
  loans: PerformanceLoanDetail[];
}

export interface PerformanceTrendMetric {
  current: PerformanceAggregate;
  comparison: PerformanceAggregate;
}

export interface PerformanceTrendPoint {
  key: string;
  label: string;
  comparisonLabel: string;
  hasCurrent?: boolean;
  hasComparison?: boolean;
  metrics: Record<PerformanceMetricKey, PerformanceTrendMetric>;
}

export interface PerformancePeriodCell {
  units: number;
  volume: number;
  loans: PerformanceLoanDetail[];
}

export interface PerformancePeriodRow {
  key: string;
  label: string;
  businessDays: number;
  metrics: Record<PerformanceMetricKey, PerformancePeriodCell>;
}

export interface CohortStageValue {
  units: number;
  volume: number;
  percentage: number;
  loans: PerformanceLoanDetail[];
}

export interface CohortFolderValue {
  folder: string;
  units: number;
  volume: number;
  percentage: number;
  loans: PerformanceLoanDetail[];
}

export interface PerformanceCohortRow {
  key: string;
  label: string;
  cohort: CohortStageValue;
  application: CohortStageValue;
  locked: CohortStageValue;
  funded: CohortStageValue;
  anomalyCount: number;
}

export interface PerformanceCohortSeries {
  rows: PerformanceCohortRow[];
  folderDistribution: CohortFolderValue[];
  anomalyCount: number;
}

export interface PipelinePerformanceViewModel {
  currentRange: PerformanceRange;
  comparisonRange: PerformanceRange;
  summaries: Record<PerformanceMetricKey, PerformanceMetricSummary>;
  trend: PerformanceTrendPoint[];
  periods: PerformancePeriodRow[];
  cohorts: Record<CohortBasis, PerformanceCohortSeries>;
  warnings: string[];
}

export interface PipelinePerformanceFilters {
  loanOfficer: string;
  investor: string;
  channel: string;
  product: string;
}

export interface PipelinePerformanceFilterOptions {
  loanOfficers: string[];
  investors: string[];
  channels: string[];
  products: string[];
}

export interface PipelinePerformanceExportInput {
  model: PipelinePerformanceViewModel;
  loans: PipelineLoan[];
  filters: PipelinePerformanceFilters;
  refreshedAt?: Date | null;
}
